// Pastel folder colors - inspired by macOS folders and creative palettes
// Each color includes a main color and a complementary label background/text combo

export interface FolderColorPalette {
  id: string;
  name: string;
  folder: string;      // Main folder color (HSL)
  folderDark: string;  // Slightly darker shade for depth
  label: string;       // Label background color
  labelText: string;   // Label text color
}

export const FOLDER_COLORS: FolderColorPalette[] = [
  {
    id: 'periwinkle',
    name: 'Periwinkle',
    folder: 'hsl(225, 60%, 75%)',
    folderDark: 'hsl(225, 55%, 65%)',
    label: 'hsl(45, 80%, 80%)',
    labelText: 'hsl(30, 40%, 25%)',
  },
  {
    id: 'sage',
    name: 'Sage',
    folder: 'hsl(140, 40%, 70%)',
    folderDark: 'hsl(140, 35%, 58%)',
    label: 'hsl(35, 75%, 85%)',
    labelText: 'hsl(140, 30%, 25%)',
  },
  {
    id: 'coral',
    name: 'Coral',
    folder: 'hsl(15, 70%, 75%)',
    folderDark: 'hsl(15, 65%, 63%)',
    label: 'hsl(45, 90%, 88%)',
    labelText: 'hsl(15, 50%, 30%)',
  },
  {
    id: 'lavender',
    name: 'Lavender',
    folder: 'hsl(270, 50%, 78%)',
    folderDark: 'hsl(270, 45%, 66%)',
    label: 'hsl(55, 80%, 85%)',
    labelText: 'hsl(270, 35%, 30%)',
  },
  {
    id: 'mint',
    name: 'Mint',
    folder: 'hsl(165, 50%, 72%)',
    folderDark: 'hsl(165, 45%, 60%)',
    label: 'hsl(40, 85%, 88%)',
    labelText: 'hsl(165, 35%, 25%)',
  },
  {
    id: 'butter',
    name: 'Butter',
    folder: 'hsl(48, 80%, 78%)',
    folderDark: 'hsl(48, 75%, 66%)',
    label: 'hsl(28, 65%, 85%)',
    labelText: 'hsl(28, 55%, 25%)',
  },
  {
    id: 'rose',
    name: 'Rose',
    folder: 'hsl(340, 55%, 78%)',
    folderDark: 'hsl(340, 50%, 66%)',
    label: 'hsl(50, 85%, 88%)',
    labelText: 'hsl(340, 40%, 30%)',
  },
  {
    id: 'sky',
    name: 'Sky',
    folder: 'hsl(200, 65%, 75%)',
    folderDark: 'hsl(200, 60%, 63%)',
    label: 'hsl(40, 80%, 87%)',
    labelText: 'hsl(200, 40%, 25%)',
  },
  {
    id: 'peach',
    name: 'Peach',
    folder: 'hsl(25, 75%, 78%)',
    folderDark: 'hsl(25, 70%, 66%)',
    label: 'hsl(55, 85%, 90%)',
    labelText: 'hsl(25, 45%, 28%)',
  },
  {
    id: 'slate',
    name: 'Slate',
    folder: 'hsl(210, 25%, 70%)',
    folderDark: 'hsl(210, 22%, 58%)',
    label: 'hsl(45, 70%, 88%)',
    labelText: 'hsl(210, 20%, 25%)',
  },
];

// Get a random folder color palette
export function getRandomFolderColor(): FolderColorPalette {
  const randomIndex = Math.floor(Math.random() * FOLDER_COLORS.length);
  return FOLDER_COLORS[randomIndex];
}

// Get folder color by ID (or return default periwinkle)
export function getFolderColorById(id: string | undefined): FolderColorPalette {
  if (!id) return FOLDER_COLORS[0];
  return FOLDER_COLORS.find(c => c.id === id) || FOLDER_COLORS[0];
}

// Get folder color from hex/hsl string (tries to match closest)
export function getFolderColorFromValue(value: string | undefined): FolderColorPalette {
  if (!value) return FOLDER_COLORS[0];
  
  // First try to find by ID
  const byId = FOLDER_COLORS.find(c => c.id === value);
  if (byId) return byId;
  
  // Then try to find by folder color
  const byColor = FOLDER_COLORS.find(c => c.folder === value);
  if (byColor) return byColor;
  
  // Default to first
  return FOLDER_COLORS[0];
}
